import { type PropsWithChildren, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { VList, WindowVirtualizer } from 'virtua';
import { ContactListItem } from '@/components/contacts/contact-list/contact-list-item';
import VirtuaItem from '@/components/virtua/virtua-item';
import { type SortFriendReducerFilterType, sortFriendByFilterType } from '@/hooks/contacts/use-sorted-friends';
import type { FuzzySearchResult } from '@/hooks/search/use-search-friends';
import useConversationSessions from '@/hooks/sessions/use-conversation-sessions';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { Friend } from '@/models/user/friend';
import ROUTES from '@/routes';
import { handleDisplaySessionMessages } from '@/routes/messages/sessions/_layout.loader';
import { addTranslateToast } from '@/stores/toast';
import cn from '@/utils/merge-class-names';
import SortContactDropdown from './sort-contact-dropdown';

interface Props {
  friends: Array<Friend & { FuzzySearch?: FuzzySearchResult }>;
  // ids should be selected and cannot unselect
  residentIds?: number[];
  selectedIds?: number[];
  disabledIds?: number[];
  maxCount?: number;

  onSelect?: (friendIds: number[]) => void;

  className?: string;
  itemClassName?: string;

  canSelectAll?: boolean;
  sortable?: boolean;
  showOnlineDesc?: boolean;

  virtualizeAs?: 'window' | 'regular';
}

export default function ContactList({
  friends,
  onSelect,
  selectedIds = [],
  disabledIds = [],
  residentIds = [],
  maxCount,
  itemClassName,
  virtualizeAs = 'regular',
  canSelectAll = false,
  sortable = true,
  showOnlineDesc = true,
}: Props) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<SortFriendReducerFilterType>('by-name');
  const { availableSessions: recentSessions } = useConversationSessions();
  const isDesktop = useIsDesktop();

  const singleSelect = maxCount === 1;
  const selectableFriends = useMemo(
    () => sortFriendByFilterType(sortBy, friends, recentSessions),
    [sortBy, friends, recentSessions],
  );

  // Check if all selectable friends are currently selected
  const isAllSelected = useMemo(
    () =>
      (maxCount && selectedIds.length + residentIds.length >= maxCount) ||
      (selectableFriends.length > 0 && selectedIds.length >= selectableFriends.length),
    [selectableFriends.length, selectedIds.length, residentIds.length, maxCount],
  );

  // Handle select all / deselect all functionality
  const handleSelectAll = () => {
    if (!onSelect) return;

    if (isAllSelected) {
      // Deselect all: remove all currently selectable friends from selection
      const newSelectedIds = selectedIds.filter((id) => !selectableFriends.some((friend) => friend.FriendID === id));
      onSelect(newSelectedIds);
    } else {
      // Select all: add all currently selectable friends to selection
      const selectableIds = selectableFriends
        .map((friend) => friend.FriendID)
        .filter((id) => !(disabledIds.includes(id) || residentIds.includes(id)));
      const newSelectedIds = new Set(selectedIds);

      // Check if selection exceeds the maximal allowed count
      if (maxCount) {
        while (newSelectedIds.size + residentIds.length <= maxCount) {
          if (newSelectedIds.size + residentIds.length === maxCount) {
            addTranslateToast({ type: 'warning', message: `conversation.forward.max-selected|count=${maxCount}` });
            break;
          }
          const nextSelectableId = selectableIds.shift();
          if (!nextSelectableId) break;
          if (residentIds.includes(nextSelectableId)) continue;
          if (disabledIds.includes(nextSelectableId)) continue;
          newSelectedIds.add(nextSelectableId);
        }
      } else {
        for (const selectableId of selectableIds) {
          newSelectedIds.add(selectableId);
        }
      }

      onSelect(Array.from(newSelectedIds));
    }
  };

  const handleSelectFriend = (friend: Friend) => {
    if (!onSelect || disabledIds.includes(friend.FriendID) || residentIds.includes(friend.FriendID)) return;

    if (singleSelect) {
      onSelect([friend.FriendID]);
      return;
    }

    if (selectedIds.includes(friend.FriendID)) {
      // Remove friend from selection
      onSelect(selectedIds.filter((id) => id !== friend.FriendID));
    } else {
      // Add friend to selection
      if (maxCount && selectedIds.length + residentIds.length >= maxCount) {
        addTranslateToast({
          type: 'warning',
          message: `conversation.forward.max-selected|count=${maxCount}`,
        });
        return;
      }
      onSelect([...selectedIds, friend.FriendID]);
    }
  };

  return (
    <>
      <div className='flex mx-5 mb-2 items-center justify-between h-6'>
        <span className='text-sm font-bold'>{`${t('conversation.contact.contact')} (${selectableFriends.length})`}</span>
        {sortable ? (
          <SortContactDropdown
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        ) : (
          <div />
        )}
      </div>

      {canSelectAll && selectableFriends.length > 0 && onSelect && (
        <div className='my-2 gap-2 mx-5 flex items-center justify-end'>
          <span className='text-sm text-secondary'>
            {t('common.select-all')}{' '}
            {maxCount !== undefined && `(${selectedIds.length + residentIds.length}/${maxCount})`}
          </span>
          <input
            type='checkbox'
            readOnly
            checked={isAllSelected}
            onChange={handleSelectAll}
            className={cn('mr-2 ms-2 rounded-full border-base-200', {
              'radio-primary radio': singleSelect,
              'checkbox-primary checkbox': !singleSelect,
            })}
          />
        </div>
      )}

      <Virtualizer virtualizeAs={virtualizeAs}>
        {selectableFriends.map((friend: Friend) => {
          const isResident = residentIds.includes(friend.FriendID);
          const isSelected = selectedIds.includes(friend.FriendID);
          const isDisabled = disabledIds.includes(friend.FriendID);

          if (onSelect)
            return (
              <div
                key={`contact-${friend.FriendID}`}
                id={`contact-${friend.FriendID}`}
                className={cn('group/contact py-2 flex items-center rounded-box', itemClassName, {
                  'group-hover/contact:[&_div]:bg-base-100': !isDisabled,
                })}
                onClick={() => handleSelectFriend(friend)}>
                <ContactListItem
                  item={friend}
                  className='py-0'
                  showOnlineDesc={showOnlineDesc}
                  suffix={
                    <input
                      type='checkbox'
                      readOnly
                      disabled={isDisabled || isResident}
                      checked={isSelected || isResident}
                      className={cn('mr-2 ms-2 rounded-full border-base-200', {
                        'radio-primary radio': singleSelect,
                        'checkbox-primary checkbox': !singleSelect,
                      })}
                    />
                  }
                />
              </div>
            );

          return (
            <div
              key={`contact-${friend.FriendID}`}
              id={`contact-${friend.FriendID}`}
              className={cn('group/contact', itemClassName)}>
              <NavLink
                className={({ isActive }) =>
                  cn('group-hover/contact:bg-base-100 block rounded-box', { '*:bg-base-100': isActive })
                }
                to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({ params: { userId: friend.FriendID } })}
                onClick={async (e) => {
                  if (isDesktop) {
                    e.preventDefault();
                    if (friend?.ChatSessionID) await handleDisplaySessionMessages(friend.ChatSessionID);
                  }
                }}
                state={ROUTES.CONTACT.USERS.BY_ID.$buildState({ state: { from: 'contact' } })}>
                <ContactListItem
                  item={friend}
                  showOnlineDesc={showOnlineDesc}
                />
              </NavLink>
            </div>
          );
        })}
      </Virtualizer>
    </>
  );
}

const Virtualizer = ({ children, virtualizeAs }: PropsWithChildren<Pick<Props, 'virtualizeAs'>>) => {
  if (virtualizeAs === 'window') {
    return (
      <div className='flex flex-1 flex-col items-stretch pb-safe'>
        <WindowVirtualizer item={VirtuaItem}>{children}</WindowVirtualizer>
      </div>
    );
  }

  return (
    <VList
      className='flex flex-1 flex-col items-stretch pb-safe'
      item={VirtuaItem}
      itemSize={64}
      overscan={6}>
      {children}
    </VList>
  );
};
